// Grouping of a rep's forecast buckets for the Plan grid.
//
// A rep can carry ~100 buckets, because the categorisation pass splits each
// account's work by job-family × recurring × work-mode. Rendered flat that's
// unreadable. This headless helper folds the flat list into the structure the
// grid renders as collapsible twisties:
//
//   Section (tier)            Strategic / Key / Client work / New business
//     └─ Group                an account (strategic/key) or a job-family
//        │                    (client tier is portfolio-pooled, so it has no
//        │                    per-customer key and groups by job-family)
//        └─ Bucket            the individual bucket, labelled by its grain
//                             (job-family / "Other work") with recurring +
//                             work-mode chips
//
// Pure and platform-neutral — no rendering, no styling. The shell decides how
// to draw sections, twisties, and chips; this file decides membership, order,
// labels, and roll-ups. Sort/misc-last/singleton rules live here so they are
// unit-testable independent of the DOM.

import type { PlanGridBucketRow } from "../data/api-types";

export type BucketSection = "strategic" | "key" | "client" | "new_business";

/** Fixed top-to-bottom section order: money and strategic focus first. */
const SECTION_ORDER: BucketSection[] = [
  "strategic",
  "key",
  "client",
  "new_business",
];

const SECTION_LABELS: Record<BucketSection, string> = {
  strategic: "Strategic accounts",
  key: "Key accounts",
  client: "Client work",
  new_business: "New business",
};

const WORK_MODE_CHIP: Record<string, string> = {
  pod: "On-demand",
  stock_customer: "Customer-stock",
  stock_ours: "Company-stock",
};

export interface GroupedBucket {
  bucket: PlanGridBucketRow;
  /** Short label for the row inside its group (job-family / "Other work"). */
  label: string;
  /** Recurring + work-mode qualifiers, rendered as chips. */
  chips: string[];
}

export interface BucketGroup {
  key: string;
  /** Account name, job-family, or section label (for the New business group). */
  label: string;
  section: BucketSection;
  /** One-bucket groups render flat (no twistie) — see the grid. */
  isSingleton: boolean;
  buckets: GroupedBucket[];
  adjustedGbp: number;
  adjustmentsGbp: number;
  actualsYtdGbp: number;
  lockedCount: number;
  totalCount: number;
}

export interface BucketSectionGroup {
  section: BucketSection;
  label: string;
  groups: BucketGroup[];
}

function sectionOf(b: PlanGridBucketRow): BucketSection {
  if (b.is_new_business) return "new_business";
  if (b.tier === "strategic" || b.tier === "key" || b.tier === "client") {
    return b.tier;
  }
  // No tier and not New Business is an anomaly (every real bucket carries a
  // tier); fall back to Client work so it stays visible rather than dropped.
  return "client";
}

/** Group key + display label for the bucket's group within its section. */
function groupIdentity(b: PlanGridBucketRow): { key: string; label: string } {
  const section = sectionOf(b);
  if (section === "new_business") {
    return { key: "new_business", label: SECTION_LABELS.new_business };
  }
  if (section === "client") {
    // Client work is portfolio-pooled — no per-customer key — so it sub-groups
    // by job-family, with the misc pool as its own "Other work" group.
    const jf = b.is_miscellaneous ? null : b.job_family;
    return {
      key: `client:jf:${jf ?? "__misc"}`,
      label: jf ?? "Other work",
    };
  }
  // Strategic / Key group by account.
  const custKey = b.customer_id ?? b.bucket_name;
  return {
    key: `${section}:cust:${custKey}`,
    label: b.customer_name ?? b.bucket_name,
  };
}

/** The short row label for a bucket shown inside an (already-labelled) group. */
export function bucketShortLabel(b: PlanGridBucketRow): string {
  if (b.is_new_business) return "New business";
  if (b.is_miscellaneous) return "Other work";
  return b.job_family ?? b.bucket_name;
}

/** Recurring + work-mode qualifier chips for a bucket row. */
export function bucketChips(b: PlanGridBucketRow): string[] {
  const chips: string[] = [];
  if (b.is_recurring) chips.push("recurring");
  const mode = b.stock_model ? WORK_MODE_CHIP[b.stock_model] : undefined;
  if (mode) chips.push(mode);
  return chips;
}

/** Buckets sort within a group: misc ("Other work") last, then value desc. */
function compareWithinGroup(a: GroupedBucket, b: GroupedBucket): number {
  const aMisc = a.bucket.is_miscellaneous ? 1 : 0;
  const bMisc = b.bucket.is_miscellaneous ? 1 : 0;
  if (aMisc !== bMisc) return aMisc - bMisc;
  return b.bucket.adjusted_gbp - a.bucket.adjusted_gbp;
}

/** Groups sort within a section by value desc; client "Other work" forced last. */
function compareGroups(a: BucketGroup, b: BucketGroup): number {
  const aOther = a.key === "client:jf:__misc" ? 1 : 0;
  const bOther = b.key === "client:jf:__misc" ? 1 : 0;
  if (aOther !== bOther) return aOther - bOther;
  return b.adjustedGbp - a.adjustedGbp;
}

/**
 * Fold a rep's flat bucket list into ordered sections → groups → buckets.
 * Empty sections are omitted. Order is stable and deterministic.
 */
export function groupPlanBuckets(
  buckets: PlanGridBucketRow[],
): BucketSectionGroup[] {
  const byGroupKey = new Map<string, BucketGroup>();

  for (const bucket of buckets) {
    const section = sectionOf(bucket);
    const { key, label } = groupIdentity(bucket);
    let group = byGroupKey.get(key);
    if (!group) {
      group = {
        key,
        label,
        section,
        isSingleton: false,
        buckets: [],
        adjustedGbp: 0,
        adjustmentsGbp: 0,
        actualsYtdGbp: 0,
        lockedCount: 0,
        totalCount: 0,
      };
      byGroupKey.set(key, group);
    }
    group.buckets.push({
      bucket,
      label: bucketShortLabel(bucket),
      chips: bucketChips(bucket),
    });
    group.adjustedGbp += bucket.adjusted_gbp;
    group.adjustmentsGbp += bucket.adjustments_gbp;
    group.actualsYtdGbp += bucket.actuals_ytd_gbp;
    if (bucket.is_locked) group.lockedCount += 1;
    group.totalCount += 1;
  }

  const sections: BucketSectionGroup[] = [];
  for (const section of SECTION_ORDER) {
    const groups = [...byGroupKey.values()].filter(
      (g) => g.section === section,
    );
    if (groups.length === 0) continue;
    for (const g of groups) {
      g.buckets.sort(compareWithinGroup);
      g.isSingleton = g.buckets.length === 1;
    }
    groups.sort(compareGroups);
    sections.push({ section, label: SECTION_LABELS[section], groups });
  }
  return sections;
}
